import { useEffect, useState } from 'react';
import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation';

import useUrlUserHomepage from '@/hooks/user/useUrlUserHomepage';
import useUser from '@/utils/hooks/swr/useUser';
import useSignUpCheck from '@/utils/hooks/user/useCurrentSession/useCheckAndRedirect/useSignUpCheck';

import { formatUrls } from '@/utils/urls/formatUrl';

import { APP_ROUTER_ROUTES, stripLocalePrefix } from '@/lib/routes/appRoutes';
import * as publicRoutes from '@/lib/routes/publicRoutes';

type UseCheckAndRedirectProps = { launchCheckRedirect: boolean; isLoggedRedirect: boolean };

/**
 * Check if a route should be handled by the App Router
 */
const isAppRouterRoute = (path: string): boolean => {
    return APP_ROUTER_ROUTES.some((route) => stripLocalePrefix(path) === route);
};

function useCheckAndRedirect({ launchCheckRedirect, isLoggedRedirect }: UseCheckAndRedirectProps) {
    const [isCheckRedirectLoading, setIsCheckRedirectLoading] = useState(launchCheckRedirect);

    const { user } = useUser();
    const { userHomepage } = useUrlUserHomepage();
    const { checkAndRedirectSignUp } = useSignUpCheck();

    const router = useRouter();
    const params = useParams();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    useEffect(() => {
        if (!launchCheckRedirect) {
            setIsCheckRedirectLoading(false);
            return;
        }

        const pathWithoutLocale = stripLocalePrefix(pathname);

        /**
         * If user is not logged in and the current page is not a public page, then show the loading screen
         */
        const publicPages = Object.values(publicRoutes);
        const formattedPublicUrls = formatUrls(publicPages, params);
        const isPublicPage = formattedPublicUrls.some(
            (url) => stripLocalePrefix(url) === pathWithoutLocale
        );

        // If user is not logged in and not on a public page, keep loading (SWRConfigWrapper will redirect)
        if (!user && !isPublicPage) {
            setIsCheckRedirectLoading(true);
            return;
        }

        // If user exists, check for signup redirects and logged redirects
        if (user) {
            const { isSignUpRedirect } = checkAndRedirectSignUp();

            if (isSignUpRedirect) {
                setIsCheckRedirectLoading(true);
                return;
            }

            if (isLoggedRedirect && stripLocalePrefix(userHomepage) !== pathname) {
                const locale = (params?.locale as string) || 'en';
                const queryString = searchParams.size ? `?${searchParams}` : '';

                // Navigate to the target route
                if (isAppRouterRoute(userHomepage)) {
                    // App Router route: use locale prefix in path
                    router.push(`/${locale}${userHomepage}${queryString}`);
                } else {
                    // Pages Router route: no locale prefix needed (i18n disabled)
                    // Use window.location for full page navigation between routers
                    window.location.href = `${userHomepage}${queryString}`;
                }
                setIsCheckRedirectLoading(true);
                return;
            }
        }

        // User is logged in and all checks passed, or user is null on a public page - allow rendering
        setIsCheckRedirectLoading(false);
    }, [
        isLoggedRedirect,
        launchCheckRedirect,
        pathname,
        router,
        searchParams,
        user,
        userHomepage,
        params
    ]);

    return { isCheckRedirectLoading };
}

export default useCheckAndRedirect;
